import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "@/lib/whatsapp";

const FAQS = [
    {
        q: "Quanto custa um site ou cardápio digital?",
        a: "Cada projeto é orçado sob medida para a realidade do seu negócio — sem pacotes engessados. Chame no WhatsApp, conte o que precisa e receba um orçamento rápido e sem compromisso.",
    },
    {
        q: "Em quanto tempo meu site fica pronto?",
        a: "Landing pages costumam ficar prontas em poucos dias. Cardápios digitais e projetos maiores levam um pouco mais, sempre com prazo combinado antes de começar.",
    },
    {
        q: "O cardápio digital cobra comissão por pedido?",
        a: "Nunca. Não cobramos taxa alguma sobre os seus pedidos — nem comissão, nem mensalidade. Você paga apenas pelo trabalho de desenvolver o projeto, e 100% das vendas ficam no seu caixa. Diferente dos apps de entrega, que cobram de 15% a 27% por pedido.",
    },
    {
        q: "Vocês atendem fora do Campo Limpo?",
        a: "Sim. O atendimento presencial é focado no Campo Limpo, Taboão da Serra e Zona Sul de SP, mas sites e cardápios digitais são feitos remotamente para qualquer região do Brasil.",
    },
    {
        q: "Como funciona o suporte técnico de informática?",
        a: "Você chama no WhatsApp, descreve o problema e recebe o diagnóstico. Dependendo do caso, resolvemos remotamente na hora ou agendamos uma visita presencial rápida.",
    },
];

export const Faq = () => {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" data-testid="faq-section" className="relative py-24 sm:py-32">
            <div className="mx-auto max-w-3xl px-5 sm:px-8">
                <Reveal className="text-center">
                    <span className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-400">
                        Perguntas Frequentes
                    </span>
                    <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                        Ficou com alguma dúvida?
                    </h2>
                    <p className="mt-4 text-base text-slate-400 sm:text-lg">
                        As respostas mais procuradas por quem é do bairro.
                    </p>
                </Reveal>

                <div className="mt-12 space-y-3">
                    {FAQS.map((f, i) => (
                        <Reveal key={f.q} delay={i * 0.07}>
                            <div
                                data-testid={`faq-item-${i}`}
                                className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${
                                    open === i ? "border-cyan-400/35 bg-[#111827]" : "border-white/[0.07] bg-[#111827]/70"
                                }`}
                            >
                                <button
                                    data-testid={`faq-question-${i}`}
                                    onClick={() => setOpen(open === i ? -1 : i)}
                                    aria-expanded={open === i}
                                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                                >
                                    <span className="text-sm font-bold text-slate-100 sm:text-base">{f.q}</span>
                                    <ChevronDown
                                        size={18}
                                        className={`shrink-0 text-cyan-300 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {open === i && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                        >
                                            <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm leading-relaxed text-slate-400">
                                                {f.a}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-10 text-center">
                    <p className="text-sm text-slate-500">Não achou sua resposta?</p>
                    <a
                        href={waLink("general")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="faq-whatsapp-button"
                        className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-3 text-sm font-bold text-[#25D366] transition-all duration-300 hover:bg-[#25D366] hover:text-[#052e12]"
                    >
                        <MessageCircle size={16} strokeWidth={2.5} />
                        Perguntar no WhatsApp
                    </a>
                </Reveal>
            </div>
        </section>
    );
};
